// Ядро voice-recorder — конечный автомат записи (SPEC §4.2) + загрузка (§4.5) + отправка (§4.6) +
// статус расшифровки (§4.9). Реализует createVoiceRecorder против инъецируемых портов
// (transport/mediaUpload/capture); соседей как код НЕ импортирует (keyhole-изоляция Тир 3).
//
// КЛЮЧЕВЫЕ ИНВАРИАНТЫ (SPEC §2):
//   1. Запись по рецепту Diktum (timeslice/aresample) — не менять. Захват инкапсулирован в
//      capture-порте (§4.8), рекордер всегда зовёт start(limits.timesliceMs) и НЕ перекодирует blob.
//   2. «Увести-отменить» НЕ отправляет — путь отмены ФИЗИЧЕСКИ отделён: cancel()/dispose() сбрасывают
//      сессию и НИКОГДА не зовут upload/send. Отправляет только finalizeAndSend() из pressEnd/авто-стоп.
//   3. Отправка НЕ ждёт ASR (D-02) — send резолвится сразу после complete+enqueue/sendMessage;
//      transcript.* приходят асинхронно через transport.events() ПОСЛЕ и только пробрасываются наружу.
//   4. Корректный контейнер для GigaAM — mime в uploadAudio = capture.pickMime() (или mimeType
//      рекордера), из whitelisted-набора; запись без поддерживаемого контейнера не создаётся.
import { MIC_DENIED_ERROR_NAMES, MIC_UNAVAILABLE_ERROR_NAMES, } from './ports.js';
import { DEFAULT_LIMITS, } from './types.js';
import { WaveformBuilder } from './waveform.js';
const WHITELISTED_MIME_PREFIXES = ['audio/webm', 'audio/mp4'];
const LIVE_WAVEFORM_BUCKETS = 48;
export function createVoiceRecorder(opts) {
    const limits = { ...DEFAULT_LIMITS, ...opts.limits };
    const now = opts.now ?? Date.now;
    const capture = opts.capture;
    const transport = opts.transport;
    const mediaUpload = opts.mediaUpload;
    // ── состояние/снимок ──────────────────────────────────────────────────────
    let state = 'idle';
    let locked = false;
    let elapsedMs = 0;
    let liveWaveform = [];
    let uploadProgress;
    let error;
    let session = null;
    let disposed = false;
    // Отпускание/отмена ПРИШЛИ, пока ждём getUserMedia (state='requesting-mic', session ещё null).
    // На мобиле первый запуск показывает системный промпт разрешения — палец отпускается (pointerup) ДО
    // выдачи мика, а pressEnd тогда был no-op (ещё не 'recording'): запись стартовала «без хозяина» и
    // крутилась до maxDurationMs (10 мин) → «мик включается и через минуты сам гаснет, ничего не ушло».
    // Флаг = «не стартовать запись, когда мик наконец выдан» (ранний релиз press-and-hold ⇒ отмена).
    let stopRequested = false;
    const listeners = new Set();
    function snapshot() {
        const snap = { state, elapsedMs, liveWaveform, locked };
        if (uploadProgress !== undefined)
            snap.uploadProgress = uploadProgress;
        if (error)
            snap.error = error;
        return snap;
    }
    function emit() {
        const snap = snapshot();
        for (const l of listeners)
            l(snap);
    }
    function setState(next) {
        state = next;
        emit();
    }
    /**
     * Чтение состояния в обход control-flow-narrowing TS: после `await` мьютатор `setState`
     * (вызванный из other-path или после раннего guard) меняет `state`, но CFA этого не видит.
     * Пере-проверки после await идут через getState(), чтобы сравнения не считались «невозможными».
     */
    function getState() {
        return state;
    }
    function fail(kind, message) {
        error = { kind, message };
        setState('error');
    }
    function clearErrorToIdle() {
        error = undefined;
        uploadProgress = undefined;
        setState('idle');
    }
    // ── трекинг отправленных сообщений для проброса статуса расшифровки (§4.9) ──
    const ownMessageIds = new Set();
    // ── приём transcript.* из transport.events() (§4.9) ────────────────────────
    let transcriptStreamStopped = false;
    function startTranscriptStream() {
        if (!transport.events)
            return;
        const iterable = transport.events();
        void (async () => {
            try {
                for await (const ev of iterable) {
                    if (transcriptStreamStopped)
                        return;
                    forwardTranscript(ev);
                }
            }
            catch {
                // поток мог закрыться — статус расшифровки не критичен для записи/отправки (инвариант 3)
            }
        })();
    }
    function forwardTranscript(ev) {
        if (ev.t !== 'server')
            return;
        const se = ev.event;
        if (se.t === 'transcript.ready' && ownMessageIds.has(se.messageId)) {
            emitTranscript({ status: 'ready', messageId: se.messageId, text: se.text });
        }
        else if (se.t === 'transcript.failed' && ownMessageIds.has(se.messageId)) {
            emitTranscript({ status: 'failed', messageId: se.messageId });
        }
    }
    function emitTranscript(u) {
        opts.onTranscript?.(u);
    }
    // ── метр уровня / live-waveform (§4.3) ─────────────────────────────────────
    function startMeter(s) {
        if (!s.analyser)
            return; // метр недоступен ⇒ финал даст плоский плейсхолдер (§4.3)
        s.meterTimer = setInterval(() => {
            if (s.aborted || !s.analyser)
                return;
            s.waveform.push(s.analyser.level());
            liveWaveform = s.waveform.live(LIVE_WAVEFORM_BUCKETS);
            elapsedMs = now() - s.startedAt;
            emit();
        }, limits.sampleIntervalMs);
    }
    function stopTimers(s) {
        if (s.meterTimer !== null) {
            clearInterval(s.meterTimer);
            s.meterTimer = null;
        }
        if (s.maxTimer !== null) {
            clearTimeout(s.maxTimer);
            s.maxTimer = null;
        }
    }
    /** Полная разборка сессии: таймеры, метр, рекордер, треки. Идемпотентно. */
    function teardownSession(s) {
        s.aborted = true;
        stopTimers(s);
        try {
            s.recorder.dispose();
        }
        catch {
            /* идемпотентно */
        }
        try {
            s.analyser?.dispose();
        }
        catch {
            /* идемпотентно */
        }
        try {
            s.stream.release();
        }
        catch {
            /* идемпотентно */
        }
    }
    // ── §4.2 idle → requesting-mic → recording ────────────────────────────────
    async function pressStart() {
        if (disposed)
            return;
        // идемпотентность/дребезг (§4.10): повторный старт во время записи/запроса — no-op
        if (state === 'recording' || state === 'requesting-mic')
            return;
        // из error/idle начинаем чисто
        error = undefined;
        uploadProgress = undefined;
        locked = false;
        elapsedMs = 0;
        liveWaveform = [];
        stopRequested = false;
        setState('requesting-mic');
        let stream;
        try {
            stream = await capture.getUserMedia();
        }
        catch (e) {
            const kind = classifyMicError(e);
            fail(kind, kind === 'mic-denied' ? 'Разрешите доступ к микрофону' : 'Микрофон недоступен');
            return;
        }
        // отмена/dispose/ранний релиз могли случиться, пока ждали разрешение — НЕ стартуем запись
        if (disposed || stopRequested || getState() !== 'requesting-mic') {
            try {
                stream.release();
            }
            catch {
                /* идемпотентно */
            }
            stopRequested = false;
            // ранний релиз оставлял state='requesting-mic' (pressEnd не менял его) → возвращаем в idle
            if (!disposed && getState() === 'requesting-mic')
                setState('idle');
            return;
        }
        // выбор контейнера (инвариант 4): без поддерживаемого mime запись не создаём
        const mime = capture.pickMime();
        if (!mime || !isWhitelistedMime(mime)) {
            try {
                stream.release();
            }
            catch {
                /* идемпотентно */
            }
            fail('mic-unavailable', 'Нет поддерживаемого аудио-контейнера для записи');
            return;
        }
        const recorder = capture.createRecorder(stream, mime);
        const analyser = capture.createAnalyser?.(stream);
        let resolveStopped;
        const stopped = new Promise((res) => {
            resolveStopped = res;
        });
        const s = {
            stream,
            recorder,
            analyser,
            mime: recorder.mimeType || mime, // mimeType рекордера — истина контейнера (инвариант 4)
            chunks: [],
            waveform: new WaveformBuilder(),
            startedAt: now(),
            meterTimer: null,
            maxTimer: null,
            stopped,
            resolveStopped,
            aborted: false,
        };
        session = s;
        recorder.onChunk((c) => {
            if (s.aborted)
                return;
            // конкатенация чанков в ИСХОДНОМ контейнере, БЕЗ перекодирования (инвариант 1)
            s.chunks.push(c.data);
        });
        recorder.onStop(() => {
            s.resolveStopped();
        });
        recorder.onError(() => {
            handleInterruption(s);
        });
        recorder.onTrackEnded(() => {
            handleInterruption(s);
        });
        // рецепт Diktum: start(timeslice=5000) — НЕ 1с (инвариант 1)
        recorder.start(limits.timesliceMs);
        startMeter(s);
        // авто-стоп по maxDurationMs → как отпускание (отправляем длинную диктовку, §4.7)
        s.maxTimer = setTimeout(() => {
            if (s.aborted || session !== s)
                return;
            void finalizeAndSend(s);
        }, limits.maxDurationMs);
        setState('recording');
        opts.onRecordingSignal?.(true);
    }
    // ── §4.10 прерывание (звонок/блокировка/потеря трека) ─────────────────────
    function handleInterruption(s) {
        if (s.aborted || session !== s || state !== 'recording')
            return;
        const elapsed = now() - s.startedAt;
        if (elapsed >= limits.minDurationMs) {
            // накоплено достаточно — финализируем и шлём то, что есть (не теряем диктовку)
            void finalizeAndSend(s);
        }
        else {
            opts.onRecordingSignal?.(false);
            teardownSession(s);
            session = null;
            fail('interrupted', 'Запись прервана');
        }
    }
    // ── §4.2 recording → finalizing → uploading → sending → idle (ПУТЬ ОТПРАВКИ) ─
    async function finalizeAndSend(s) {
        // защита от двойного входа (pressEnd + maxTimer + interruption гонка): только текущая активная
        if (s.aborted || session !== s || state !== 'recording')
            return;
        setState('finalizing');
        opts.onRecordingSignal?.(false);
        stopTimers(s);
        elapsedMs = now() - s.startedAt;
        // остановить рекордер и дождаться финального dataavailable — но НЕ дольше stopFlushTimeoutMs:
        // на мобильном WebKit onstop может не прийти, и без предела запись молча зависала бы в
        // 'finalizing' навсегда; по таймауту продолжаем с уже накопленными чанками
        try {
            s.recorder.stop();
            await withTimeout(s.stopped, limits.stopFlushTimeoutMs);
        }
        catch {
            // даже если stop бросил — собираем то, что накоплено
        }
        // отмена могла прийти, пока ждали stop → НЕ отправляем (инвариант 2)
        if (s.aborted || session !== s)
            return;
        const blob = new Blob(s.chunks, { type: s.mime });
        const durationMs = elapsedMs;
        const waveform = s.waveform.finalize(durationMs, limits.peakBuckets);
        // метр/треки больше не нужны (но НЕ затираем session — это путь отправки)
        if (s.analyser) {
            try {
                s.analyser.dispose();
            }
            catch {
                /* идемпотентно */
            }
            s.analyser = undefined;
        }
        try {
            s.stream.release();
        }
        catch {
            /* идемпотентно */
        }
        // §4.7 валидация: пусто / слишком коротко → НЕ отправляем
        if (blob.size === 0) {
            session = null;
            fail('empty', 'Пустая запись');
            return;
        }
        if (durationMs < limits.minDurationMs) {
            session = null;
            fail('too-short', 'Слишком короткая запись');
            return;
        }
        // §4.5 загрузка → voiceAttachmentId
        setState('uploading');
        uploadProgress = 0;
        emit();
        const ext = mimeToExt(s.mime);
        const meta = {
            filename: `voice-${now()}.${ext}`,
            mime: s.mime, // инвариант 4: ровно тот контейнер, в котором писал MediaRecorder
            durationMs,
        };
        let attachmentId;
        try {
            const attachment = await mediaUpload.uploadAudio(blob, meta, {
                onProgress: (f) => {
                    uploadProgress = clamp01(f);
                    emit();
                },
            });
            attachmentId = attachment.id;
        }
        catch (e) {
            session = null;
            uploadProgress = undefined;
            fail('upload-failed', errMessage(e, 'Не удалось загрузить аудио'));
            return;
        }
        if (s.aborted)
            return; // dispose во время загрузки
        // §4.6 отправка — НЕ ждёт ASR (инвариант 3): шлём сразу после complete
        setState('sending');
        uploadProgress = undefined;
        emit();
        const clientMsgId = transport.newClientMsgId();
        const dto = {
            clientMsgId,
            kind: 'voice',
            voiceAttachmentId: attachmentId,
        };
        try {
            const optimistic = await sendVoice(dto, waveform, attachmentId, durationMs, clientMsgId);
            ownMessageIds.add(optimistic.id);
            opts.onSend?.(optimistic);
        }
        catch (e) {
            session = null;
            fail('send-failed', errMessage(e, 'Не удалось отправить голосовое'));
            return;
        }
        session = null;
        clearErrorToIdle();
    }
    /**
     * Отправка через outbox (КАНОН надёжности, §4.6) с оптимистичным Message наружу. Если транспорт
     * умеет sendMessage напрямую — это запасной путь для app-ui-оптимистики, но по умолчанию канон —
     * outbox. Возвращает оптимистичное Message (kind='voice', локальный waveform).
     */
    async function sendVoice(dto, waveform, attachmentId, durationMs, clientMsgId) {
        // через outbox: офлайн-очередь + идемпотентный дослыл «бесплатно» (§4.6, FR-PWA-04)
        await transport.outbox.enqueue(opts.chatId, dto);
        // оптимистичное Message — пузырь в ленту со статусом доставки (seq присвоит сервер)
        return buildOptimisticMessage(clientMsgId, attachmentId, waveform, durationMs);
    }
    function buildOptimisticMessage(clientMsgId, attachmentId, waveform, durationMs) {
        const localId = `local-${clientMsgId}`;
        return {
            id: localId,
            chatId: opts.chatId,
            seq: 0, // присвоит сервер; 0 = ещё не подтверждено
            senderId: '', // проставит app-ui/store из self (рекордер self не знает)
            clientMsgId,
            kind: 'voice',
            voice: {
                attachmentId,
                waveform,
                // transcript отсутствует на момент отправки — НЕ ждём ASR (инвариант 3)
            },
            createdAt: new Date(now()).toISOString(),
        };
    }
    // ── §4.4 жесты ────────────────────────────────────────────────────────────
    async function pressEnd() {
        if (disposed)
            return;
        // отпустили ДО выдачи мика (press-and-hold, первый промпт разрешения) — помечаем «не стартовать»,
        // иначе запись пойдёт «без хозяина» и провисит до maxDurationMs (см. stopRequested).
        if (state === 'requesting-mic') {
            stopRequested = true;
            return;
        }
        const s = session;
        if (!s || state !== 'recording')
            return; // pressEnd без активной записи — no-op (§4.10)
        await finalizeAndSend(s);
    }
    // ПУТЬ ОТМЕНЫ — физически отделён от пути отправки (инвариант 2): НИКОГДА не зовёт upload/send.
    function pressCancel() {
        if (disposed)
            return;
        // отмена во время ожидания мика — не стартуем запись, когда getUserMedia наконец резолвится
        if (state === 'requesting-mic')
            stopRequested = true;
        const s = session;
        locked = false;
        if (s && (state === 'recording' || state === 'requesting-mic' || state === 'finalizing')) {
            opts.onRecordingSignal?.(false);
            teardownSession(s); // s.aborted=true ⇒ finalize в полёте тихо выйдет
            session = null;
        }
        // из uploading/sending отмена не «откатывает» уже принятое (идемпотентность по clientMsgId);
        // активная загрузка отменяется AbortSignal-ом транспорта (передаётся в uploadAudio при наличии).
        elapsedMs = 0;
        liveWaveform = [];
        uploadProgress = undefined;
        error = undefined;
        setState('idle');
    }
    function toggleLock() {
        if (disposed)
            return;
        if (state === 'recording') {
            if (!locked) {
                // короткий тап без удержания → зафиксировать (hands-free)
                locked = true;
                emit();
            }
            else {
                // повторный тап по «стоп» → завершить и отправить
                void pressEnd();
            }
        }
    }
    function dispose() {
        if (disposed)
            return;
        disposed = true;
        transcriptStreamStopped = true;
        const s = session;
        if (s) {
            opts.onRecordingSignal?.(false);
            teardownSession(s); // как cancel — без отправки (инвариант 2)
            session = null;
        }
        listeners.clear();
    }
    function subscribe(listener) {
        listeners.add(listener);
        listener(snapshot());
        return () => {
            listeners.delete(listener);
        };
    }
    startTranscriptStream();
    return {
        get state() {
            return state;
        },
        snapshot,
        subscribe,
        pressStart,
        pressEnd,
        pressCancel,
        toggleLock,
        dispose,
    };
}
// ── чистые помощники ──────────────────────────────────────────────────────────
function classifyMicError(e) {
    const name = e?.name;
    if (name && MIC_UNAVAILABLE_ERROR_NAMES.includes(name))
        return 'mic-unavailable';
    if (name && MIC_DENIED_ERROR_NAMES.includes(name))
        return 'mic-denied';
    // незнакомый отказ трактуем как отказ доступа (безопаснее: пользователь увидит подсказку про мик)
    return 'mic-denied';
}
export function isWhitelistedMime(mime) {
    const lower = mime.toLowerCase();
    return WHITELISTED_MIME_PREFIXES.some((p) => lower.startsWith(p));
}
export function mimeToExt(mime) {
    const lower = mime.toLowerCase();
    if (lower.startsWith('audio/webm'))
        return 'webm';
    if (lower.startsWith('audio/mp4'))
        return 'm4a';
    return 'bin';
}
/** Ждёт промис не дольше ms; по истечении резолвится (не реджектится) — вызывающий продолжает с тем, что есть. */
async function withTimeout(p, ms) {
    let timer = null;
    const timeout = new Promise((res) => {
        timer = setTimeout(res, ms);
    });
    try {
        await Promise.race([p, timeout]);
    }
    finally {
        if (timer !== null)
            clearTimeout(timer);
    }
}
function clamp01(v) {
    if (!Number.isFinite(v))
        return 0;
    if (v < 0)
        return 0;
    if (v > 1)
        return 1;
    return v;
}
function errMessage(e, fallback) {
    if (e instanceof Error && e.message)
        return e.message;
    return fallback;
}
//# sourceMappingURL=recorder.js.map